import React, { useMemo } from "react";
import { Modal, Table, Empty, Descriptions } from "antd";
import { UserOutlined } from "@ant-design/icons";
import moment from "moment";

const InventoryActivityLogModal = ({ open, onCancel, inventoryId, activityData, loading }) => {
  // Prepare table data from inventory detail
  const tableData = useMemo(() => {
    if (!activityData) return [];
    
    const data = [];
    const inventoryQuantities = activityData.inventory_quantity || [];
    const colorCloth = activityData.color_cloth || {};
    
    // Extract color and cloth_type directly from color_cloth (serializer flattens the structure)
    const color = colorCloth.color || 'N/A';
    const fabricType = colorCloth.cloth_type || 'N/A';
    
    // Add initial creation entry if created_by exists
    if (activityData.added_by) {
      data.push({
        key: 'created',
        serialNo: data.length + 1,
        color: color,
        fabricType: fabricType,
        lastUpdatedDate: activityData.created_at ? moment(activityData.created_at).format('YYYY-MM-DD') : 'N/A',
        whatUpdated: 'Inventory Created',
        previousValue: 'N/A',
        whoUpdated: activityData.added_by || 'System',
        rate: 'N/A',
        quantity: activityData.quantity || 0,
        timestamp: activityData.created_at, // Store timestamp for final sorting
      });
    }
    
    // Add entries from inventory_quantity array
    // First sort by created_at ascending (oldest first) to calculate previous values correctly
    const sortedQuantitiesAsc = [...inventoryQuantities].sort((a, b) => {
      return moment(a.created_at) - moment(b.created_at);
    });
    
    // Calculate cumulative previous quantities
    sortedQuantitiesAsc.forEach((item, index) => {
      // Previous quantity is the sum of all quantities added before this one
      let previousQuantity = 0;
      for (let i = 0; i < index; i++) {
        previousQuantity += parseFloat(sortedQuantitiesAsc[i].quantity || 0);
      }
      
      data.push({
        key: `quantity-${item.id || index}`,
        serialNo: data.length + 1,
        color: color,
        fabricType: fabricType,
        lastUpdatedDate: item.created_at ? moment(item.created_at).format('YYYY-MM-DD') : 'N/A',
        whatUpdated: 'Quantity Added',
        previousValue: previousQuantity.toFixed(2),
        whoUpdated: activityData.updated_by || activityData.added_by || 'System',
        rate: item.rate || 0,
        quantity: item.quantity || 0,
        timestamp: item.created_at, // Store timestamp for final sorting
      });
    });
    
    // Add main inventory update entry if last_updated_date exists
    if (activityData.last_updated_date && activityData.updated_by) {
      data.push({
        key: 'main-update',
        serialNo: data.length + 1,
        color: color,
        fabricType: fabricType,
        lastUpdatedDate: moment(activityData.last_updated_date).format('YYYY-MM-DD'),
        whatUpdated: 'Quantity Updated',
        previousValue: activityData.previous_quantity || 0,
        whoUpdated: activityData.updated_by || 'System',
        rate: 'N/A',
        quantity: activityData.quantity || 0,
        timestamp: activityData.last_updated_date, // Store timestamp for final sorting
      });
    }
    
    // Sort by timestamp descending (newest first), then update serial numbers
    const sortedData = data.sort((a, b) => {
      if (!a.timestamp && !b.timestamp) return 0;
      if (!a.timestamp) return 1;
      if (!b.timestamp) return -1;
      return moment(b.timestamp) - moment(a.timestamp);
    });
    
    // Update serial numbers after sorting
    return sortedData.map((item, index) => ({
      ...item,
      serialNo: index + 1,
    }));
  }, [activityData]);
  
  // Table columns
  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'serialNo',
      key: 'serialNo',
      width: 80,
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      width: 150,
    },
    {
      title: 'Fabric Type',
      dataIndex: 'fabricType',
      key: 'fabricType',
      width: 150,
    },
    {
      title: 'Last Updated Date',
      dataIndex: 'lastUpdatedDate',
      key: 'lastUpdatedDate',
      width: 150,
    },
    {
      title: 'What Updated',
      dataIndex: 'whatUpdated',
      key: 'whatUpdated',
      width: 150,
    },
    {
      title: 'Previous Value',
      dataIndex: 'previousValue',
      key: 'previousValue',
      width: 120,
      render: (text) => text !== 'N/A' ? parseFloat(text).toFixed(2) : text,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (text) => text !== 'N/A' ? parseFloat(text).toFixed(2) : text,
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      width: 100,
      render: (text) => text !== 'N/A' ? parseFloat(text).toFixed(2) : text,
    },
    {
      title: 'Who Updated',
      dataIndex: 'whoUpdated',
      key: 'whoUpdated',
      width: 150,
      render: (text) => (
        <span>
          <UserOutlined style={{ marginRight: 4 }} />
          {text}
        </span>
      ),
    },
  ];

  const handleCancel = () => {
    onCancel();
  };

  return (
    <>
      <style>
        {`
          .inventory-activity-log-modal .ant-modal-content {
            border-radius: 12px !important;
            overflow: hidden;
            max-height: 100vh;
            display: flex;
            flex-direction: column;
          }
          .inventory-activity-log-modal .ant-modal-header {
            border-radius: 12px 12px 0 0 !important;
            flex-shrink: 0;
          }
          .inventory-activity-log-modal .ant-modal-body {
            border-radius: 0 0 12px 12px !important;
            overflow-y: auto;
            flex: 1;
            max-height: calc(100vh - 110px);
          }
        `}
      </style>
      <Modal
        title={`Activity Log - Inventory #${inventoryId}`}
        open={open}
        onCancel={handleCancel}
        footer={null}
        width={1200}
        destroyOnClose
        rootClassName="inventory-activity-log-modal"
        centered
        styles={{
          content: { 
            borderRadius: "12px",
            overflow: "hidden",
            maxHeight: "90vh"
          },
          body: { 
            borderRadius: "12px",
            maxHeight: "calc(90vh - 110px)",
            overflowY: "auto",
            padding: "24px"
          }
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Empty description="Loading activity logs..." />
          </div>
        ) : !activityData || tableData.length === 0 ? (
          <Empty description="No activity logs found for this inventory item" />
        ) : (
          <div>
            <Descriptions bordered size="small" style={{ marginBottom: 20 }}>
              <Descriptions.Item label="Color">
                {activityData.color_cloth?.color || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Fabric Type">
                {activityData.color_cloth?.cloth_type || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Current Quantity">
                {activityData.quantity || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Total Activities">
                {tableData.length}
              </Descriptions.Item>
            </Descriptions>

            <Table
              columns={columns}
              dataSource={tableData}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} entries`,
              }}
              scroll={{ x: 1000 }}
              bordered
              size="small"
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default InventoryActivityLogModal;

